/**
 * URL Utilities Module
 * 
 * Collection of URL parsing and manipulation utilities.
 * Provides functions for working with query parameters, URL construction,
 * and GUID generation.
 * 
 * Migrated from app/frontend/scripts/lib-legacy/utils.js
 */
import { paramStringToObject } from '~/scripts/lib/utils/params';

/**
 * Helper function for GUID generation
 * Generates a random 4-character hexadecimal string
 * @private
 */
function S4() {
  // TODO: SonarCloud throws Make sure that using this pseudorandom number generator is safe here for Math.random()
  return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1); // NOSONAR
}

/**
 * Parse query parameters from a URL string or current location
 * @param {Function|null} callback - Optional callback to transform each parameter
 * @param {string} url - Optional URL to parse, defaults to window.location.search
 * @returns {Object} Object containing all query parameters as key-value pairs
 */
export function queryParams(callback, url) {
  const searchStr = url || window.location.search;
  if (typeof searchStr === "undefined" || searchStr === "" || searchStr.indexOf('?') === -1) {
    return {};
  }

  const obj = paramStringToObject(searchStr.slice(searchStr.indexOf('?') + 1), callback);
  return obj;
}

/**
 * Add or update query parameters in a URL string
 * @param {string} url - The base URL
 * @param {Object} paramsObj - Object containing parameters to add/update
 * @param {boolean} URIEncode - Whether to URI encode the parameters
 * @returns {string} URL with updated query parameters
 */
export function addParamsToUrlString(url, paramsObj, URIEncode) {
  let existingParamsObj;

  if (url.indexOf("?") > -1) {
    existingParamsObj = queryParams(null, url);
  } else {
    existingParamsObj = {};
  }

  // Add new keys
  for (const key in paramsObj) {
    existingParamsObj[key] = paramsObj[key];
  }

  // Remove all params from URL string
  if (url.indexOf("?") > -1) {
    url = url.substring(0, url.indexOf("?"));
  }

  // Add new combined set of params
  url = url + "?" + objectToParamString(existingParamsObj, URIEncode);

  return url;
}

export function locationWithNewParams(params, paramsToDel = []) {
  let queryObj, url;

  if (typeof(params) === "string") {
    params = paramStringToObject(params);
  }

  queryObj = queryParams();
  paramsToDel.forEach(function (param) {
    delete queryObj[param];
  });

  for (const key in params) {
    queryObj[key] = params[key];
  }

  const queryParamsStr = $.param(queryObj);

  url = window.location.pathname + "?" + queryParamsStr;

  if (window.location.hash !== "") {
    url += window.location.hash;
  }

  return url;
}

/**
 * Generate a globally unique identifier (GUID)
 * Format: xxxxxxxx-xxxx-4xxx-xxxx-xxxxxxxxxxxx
 * @returns {string} A lowercase GUID string
 */
export function guid() {
  return (S4() + S4() + "-" + S4() + "-4" + S4().substr(0, 3) + "-" + S4() + "-" + S4() + S4() + S4()).toLowerCase();
}

export { paramStringToObject } from '~/scripts/lib/utils/params';

/**
 * Convert an object to a query parameter string
 * @param {Object} obj - Object to convert to query string
 * @param {boolean} URIEncode - Whether to URI encode the parameters (default: true)
 * @returns {string} Query parameter string (without leading '?')
 */
export function objectToParamString(obj, URIEncode) {
  const toEncode = URIEncode === true || URIEncode === undefined ? true : false;

  return Object.keys(obj).map(function(key) {
    let value;

    if (toEncode) {
      value = encodeURIComponent(key) + '=' + encodeURIComponent(obj[key]);
    } else {
      value = key + '=' + obj[key];
    }

    return value;
  }).join('&');
}
